<template>
  <div :class="$style.container">
    <GmapMap
      ref="mapRef"
      :map-type-id="mapTypeId"
      :center="mapCenter"
      :zoom="zoom"
      :style="mapStyle"
      :options="mapOptions"
    >
      <GmapMarker
        v-for="marker in markers"
        :key="marker.id"
        :position="marker.geolocation"
        :clickable="true"
        :icon="{
          url: getMarkerIcon(marker.iconColor),
          scaledSize: { width: 24, height: 32 }
        }"
        @click="toggleInfoWindow(marker)"
      />

      <MapInfoWindow
        :details="infoWindow.details"
        :options="infoWindowOptions"
        :position="infoWindow.geolocation"
        :opened="infoWindow.opened"
        @closeclick="handleInfoWindowClose"
        @displayDetails="displayDetails"
        :useForm="useForm"
		:jobOfferJSONURL="jobOfferJSONURL"
		:filterKeyValue="filterKeyValue"
      />

      <GmapMarker
        v-if="currentPosition"
        :position="currentPosition"
        :icon="{
          url: getPositionIcon(),
          scaledSize: { width: 32, height: 32 }
        }"
      />

      <div slot="visible">
        <div :class="$style.positionControl" v-if="!initializing">
          <button
            ref="positionBtn"
            :disabled="loadingPosition"
            :class="{
              [$style.disabled]: !currentPositionAvailable
            }"
            @click="handlePositionIconClick"
          >
            <Loading
              v-if="loadingPosition"
              :class="$style.spinner"
              :size="20"
              backgroundColor="transparent"
            />
            <PositionIcon v-else />
          </button>
        </div>
      </div>
    </GmapMap>
    <MapLegend :items="legendItems" />
  </div>
</template>

<script>
import { Map as GmapMap, Marker as GmapMarker, gmapApi } from "gmap-vue";
import MapInfoWindow from "../MapInfoWindow/MapInfoWindow";
import { getCurrentPosition, getMarkerIcon, getPositionIcon } from "./helpers";
import MapLegend from "../MapLegend/MapLegend.vue";
import PositionIcon from "../../assets/svg/Position.vue";
import Loading from "../Loading/Loading.vue";

const zoomDirectionEnum = {
  UNSET: "UNSET",
  OUT: "OUT",
  IN: "IN",
  PREVENT: "PREVENT"
};

export default {
  name: "Map",
  components: {
    GmapMap,
    GmapMarker,
    MapInfoWindow,
    MapLegend,
    PositionIcon,
    Loading
  },
  props: {
    height: {
      type: String,
      default: "100%"
    },
    width: {
      type: String,
      default: "100%"
    },
    markers: {
      type: Array,
      default: () => []
    },
    mapTypeId: {
      type: String,
      default: "roadmap"
    },
    mapZoom: {
      type: Number,
      default: 10
    },
    legendItems: {
      type: Array,
      default: () => []
    },
    useForm: {
      type: Boolean,
      default: false
    },
	jobOfferJSONURL: {
      type: String,
      default: ""
    },
	filterKeyValue: {
      type: String,
      default: ""
    }
  },
  computed: {
    mapStyle: function() {
      return `height: ${this.height};width: ${this.width};`;
    },
    google: gmapApi
  },
  data() {
    return {
      initializing: true,
      loadingPosition: false,
      getPositionIcon,
      getMarkerIcon,
      dirty: false,
      mapCenter: {
        lat: -1,
        lng: -1
      },
      mapOptions: {
        disableDefaultUI: true,
        zoomControl: true
      },
      map: undefined,
      currentPosition: undefined,
      zoom: this.mapZoom,
      boundsChangedListener: undefined,
      zoomDirection: zoomDirectionEnum.UNSET,
      infoWindow: {
        opened: false,
        geolocation: undefined,
        details: undefined
      },
      infoWindowOptions: {
        pixelOffset: {
          width: 0,
          height: -35
        }
      },
      clickedMarkerId: undefined,
      currentPositionAvailable: true
    };
  },
  methods: {
    handlePositionIconClick(event) {
      event.shiftKey ? this.fakePosition() : this.checkUserPosition();
    },
    fakePosition() {
      const lat = 49.97318248358994;
      const lng = 9.185904966660072;
      this.dirty = false;
      this.loadingPosition = true;

      window.setTimeout(() => {
        this.currentPosition = {
          lat,
          lng
        };
        this.map.setCenter({
          lat,
          lng
        });
        this.checkAvailableMarker();
        this.loadingPosition = false;
      }, 2000);
    },
    handleInfoWindowClose() {
      this.infoWindow.opened = false;
    },
    toggleInfoWindow(marker) {
      if (this.clickedMarkerId == marker.id) {
        this.infoWindow.opened = !this.infoWindow.opened;
      } else {
        const { geolocation } = marker;

        this.infoWindow = {
          geolocation: geolocation,
          details: marker,
          opened: true
        };

        // delay for ipad
        window.setTimeout(() => {
          this.infoWindow.opened = false;

          window.setTimeout(() => {
            this.infoWindow.opened = true;
          }, 10);
        }, 10);

        this.clickedMarkerId = marker.id;
      }
    },
    getAllMarkerBounds() {
      if (!this.map) {
        return;
      }

      if (this.markers.length > 1) {
        const bounds = new this.google.maps.LatLngBounds();

        this.markers.forEach(marker => {
          bounds.extend(marker.geolocation);
        });

        this.$refs.mapRef.fitBounds(bounds);
      } else if (this.markers.length === 1) {
        const {
          geolocation: { lat, lng }
        } = this.markers[0];

        this.map.setCenter({
          lat,
          lng,
          zoom: this.mapZoom
        });
      }
    },
    checkUserPosition() {
      this.dirty = false;
      this.loadingPosition = true;
      getCurrentPosition()
        .then(({ lat, lng }) => {
          this.currentPosition = {
            lat,
            lng
          };

          this.map.setCenter({
            lat,
            lng
          });

          this.checkAvailableMarker();
          this.currentPositionAvailable = true;
        })
        .catch(err => {
          console.error(err);
          this.getAllMarkerBounds();
          this.$emit("loadingChange", false);
          this.initializing = false;
          this.currentPositionAvailable = false;
        })
        .finally(() => {
          this.loadingPosition = false;
        });
    },
    checkAvailableMarker() {
      const btnWidthPercent =
        60 /* button + margin */ / this.$refs.mapRef.$el.offsetWidth;
      const bounds = this.map.getBounds();
      const north = bounds.getNorthEast().lng();
      const east = bounds.getNorthEast().lat();
      const south = bounds.getSouthWest().lng();
      const west = bounds.getSouthWest().lat();
      const verticalPadding = (north - south) / 10;
      const horizontalPadding = (east - west) / 10;

      let countMarkerInView = 0;

      this.markers.forEach(marker => {
        const { lat, lng } = marker.geolocation;

        if (
          lng <= north - verticalPadding &&
          lng >= south + verticalPadding &&
          lat <= east - (east - west) * btnWidthPercent &&
          lat >= west + horizontalPadding
        ) {
          countMarkerInView++;
        }
      });

      if (!this.boundsChangedListener) {
        this.boundsChangedListener = this.map.addListener(
          "bounds_changed",
          () => {
            this.checkAvailableMarker();
          }
        );
      }

      if (this.zoomDirection !== zoomDirectionEnum.PREVENT) {
        if (
          countMarkerInView === 0 &&
          this.zoom > 0 &&
          this.zoomDirection !== zoomDirectionEnum.IN
        ) {
          this.zoomDirection = zoomDirectionEnum.OUT;
          this.zoom--;
          return;
        } else if (
          countMarkerInView > 0 &&
          this.zoom < this.mapZoom &&
          this.zoomDirection !== zoomDirectionEnum.OUT
        ) {
          this.zoomDirection = zoomDirectionEnum.IN;
          this.zoom++;
          return;
        } else if (
          countMarkerInView === 0 &&
          this.zoomDirection === zoomDirectionEnum.IN
        ) {
          this.zoomDirection = zoomDirectionEnum.PREVENT;
          this.zoom--;
          return;
        }
      }

      this.google.maps.event.removeListener(this.boundsChangedListener);
      this.zoomDirection = zoomDirectionEnum.UNSET;
      this.$emit("loadingChange", false);
      this.initializing = false;
      this.boundsChangedListener = undefined;
    },
    initDragListener() {
      this.map.addListener("dragstart", () => {
        this.dirty = true;
      });
    },
    initZoomChangeListener() {
      this.map.addListener("zoom_changed", () => {
        this.zoom = this.map.getZoom();
      });
    },
    displayDetails(url) {
      this.$emit("displayDetails", url);
    }
  },
  watch: {
    markers: function() {
      if (!this.map || this.dirty) {
        return;
      }

      this.infoWindow.opened = false;

      if (this.currentPosition) {
        this.checkAvailableMarker();
        this.map.setCenter({
          ...this.currentPosition
        });
      } else {
        this.getAllMarkerBounds();
      }
    }
  },
  mounted() {
    this.$refs.mapRef.$mapPromise.then(map => {
      this.map = map;
      this.initDragListener();
      this.initZoomChangeListener();
      this.boundsChangedListener = this.map.addListener(
        "bounds_changed",
        () => {
          this.google.maps.event.removeListener(this.boundsChangedListener);
          this.boundsChangedListener = undefined;
          this.checkUserPosition();
        }
      );
    });
  }
};
</script>

<style module lang="scss">
@import "./Map.scss";
</style>
